import { AfterViewInit, Component, ElementRef, NgModule, NgZone, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { Routes as AngularRoutes, RouterModule } from '@angular/router';
import { UniversalConfig } from '@application/universal_config.service';
import { ProgrammaticBrowserRouter } from '@cortex-shim/programmatic_browser_router.component';
import { OutletPage } from '@old_patient_dependencies/outlet_page.component';
import { LocationSelectorService } from '@public/src/application/services/location_selector.service';
import { PatientFlowContainer } from '@public/src/components/patient_flow_container.component';
import { sharedResolution } from '@public/src/shared_resolution';
import { createRoot, Root } from 'react-dom/client';
import { Route, Routes } from 'react-router';
import { ViewBooking } from '../../../patient/src/views/view_booking';
import { SecurityLevel } from '../app/Constants/securityLevel';
import { ViewGuard } from '../app/Guards/view.guard';
import { StartupService } from '../app/Services/app/startup.service';
import { CellulaProviderWrapper } from './cellula_provider_wrapper.component';

const containerElementName = 'CreateNewMessageComponent';
const FULL_HEIGHT_CLASS_NAME = 'js-fullHeight';

@Component({
	standalone: false,
	styleUrls: ['./css/create-new-message.wrapper.scss'],
	selector: 'create-new-message-component',
	template: ` <semantic-view hideOnMobile="true">
		<header-component header></header-component>

		<div class="wrapper-container" #${containerElementName}></div>

		<footer-component footer></footer-component>
	</semantic-view>`,
	encapsulation: ViewEncapsulation.None,
})
export class BookingWrapper implements OnDestroy, AfterViewInit {
	@ViewChild(containerElementName, { static: false }) containerRef: ElementRef;

	private root: Root;
	public extraHeaderHeight: boolean = false;

	constructor(private readonly ngZone: NgZone) {}

	ngAfterViewInit() {
		document.body.classList.add(FULL_HEIGHT_CLASS_NAME);
		this.render();
	}

	ngOnDestroy() {
		document.body.classList.remove(FULL_HEIGHT_CLASS_NAME);
		this.root?.unmount();
	}

	private async render() {
		const locationSelectorService = sharedResolution.resolve(LocationSelectorService);
		const UniversalConfigService = sharedResolution.resolve(UniversalConfig);

		const root = createRoot(this.containerRef.nativeElement);
		this.root = root;

		const split = locationSelectorService.location.route.split('/');
		split.splice(0, 1);
		const routeBase = split.join('/');

		this.ngZone.runOutsideAngular(() => {
			root.render(
				<CellulaProviderWrapper useGlobals>
					<PatientFlowContainer>
						<ProgrammaticBrowserRouter basename={UniversalConfigService.alias}>
							<Routes>
								<Route path={routeBase} element={<OutletPage />}>
									<Route path='booking/*' element={<ViewBooking />} />
								</Route>
							</Routes>
						</ProgrammaticBrowserRouter>
					</PatientFlowContainer>
				</CellulaProviderWrapper>,
			);
		});
	}
}

/*
	Angular routes
*/
const bookingWrapperRoutes: AngularRoutes = [
	{
		path: '',
		component: BookingWrapper,
	},
	{
		path: `who-is-respondant`,
		component: BookingWrapper,
	},
	{
		path: `reason-for-visit`,
		component: BookingWrapper,
	},
	{
		path: `location`,
		component: BookingWrapper,
	},
	{
		path: `time-slot`,
		component: BookingWrapper,
	},
	{
		path: `time-slot/:locationId`,
		component: BookingWrapper,
	},
	{
		path: `forms`,
		component: BookingWrapper,
		canActivate: [ViewGuard],
		data: { access: SecurityLevel.Client },
	},
	{
		path: `forms/:formId`,
		component: BookingWrapper,
		canActivate: [ViewGuard],
		data: { access: SecurityLevel.Client },
	},
	{
		path: `forms/:formId/:formPageId`,
		component: BookingWrapper,
		canActivate: [ViewGuard],
		data: { access: SecurityLevel.Client },
	},
	{
		path: `conclusion`,
		component: BookingWrapper,
		canActivate: [ViewGuard],
		data: { access: SecurityLevel.Client },
	},
	{
		path: `done`,
		component: BookingWrapper,
		canActivate: [ViewGuard],
		data: { access: SecurityLevel.Client },
	},
];

const bookingWrapperRouting = RouterModule.forChild(bookingWrapperRoutes);

/*
	Angular module
*/
@NgModule({
	imports: [bookingWrapperRouting],
	providers: [],
	declarations: [],
})
export class BookingWrapperModule {}
